import { Link, NavLink, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { LogOut, Plus, User as UserIcon, MapPin, MessageCircle, Settings as SettingsIcon } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

export default function Header() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [unread, setUnread] = useState(0);

  useEffect(() => {
    if (!user) { setUnread(0); return; }
    let cancelled = false;
    const tick = () => api.get("/threads/unread-count")
      .then((r) => { if (!cancelled) setUnread(r.data.count || 0); })
      .catch(() => {});
    tick();
    const t = setInterval(tick, 10_000);
    return () => { cancelled = true; clearInterval(t); };
  }, [user]);

  return (
    <header className="sticky top-0 z-50 bg-white/70 backdrop-blur-xl border-b border-white/40">
      <div className="max-w-7xl mx-auto px-5 sm:px-8 h-16 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2" data-testid="header-logo">
          <div className="h-8 w-8 rounded-full bg-[#7C3AED] grid place-items-center text-white">
            <MapPin className="h-4 w-4" />
          </div>
          <span className="font-display text-xl font-medium tracking-tight">curbside</span>
        </Link>

        <nav className="hidden md:flex items-center gap-8 text-sm">
          <NavLink to="/browse" data-testid="nav-browse"
            className={({ isActive }) => `transition-colors hover:text-[#7C3AED] ${isActive ? "text-[#7C3AED]" : "text-[#0F0F12]"}`}>
            Browse
          </NavLink>
          <NavLink to="/post" data-testid="nav-post"
            className={({ isActive }) => `transition-colors hover:text-[#7C3AED] ${isActive ? "text-[#7C3AED]" : "text-[#0F0F12]"}`}>
            Post an item
          </NavLink>
          <a href="/#how" className="text-[#0F0F12] transition-colors hover:text-[#7C3AED]">How it works</a>
        </nav>

        <div className="flex items-center gap-3">
          <Button asChild size="sm"
            className="rounded-full bg-[#7C3AED] hover:bg-[#5B21B6] text-white px-4"
            data-testid="header-post-btn">
            <Link to="/post"><Plus className="h-4 w-4 mr-1" />Post</Link>
          </Button>

          {user === undefined ? (
            <div className="h-9 w-20 rounded-full bg-[#E8E5F0] animate-pulse" />
          ) : user ? (
            <>
              <Link to="/messages" data-testid="header-messages"
                className="relative h-9 w-9 rounded-full bg-white border border-[#E8E5F0] grid place-items-center hover:border-[#7C3AED]/50 transition-colors">
                <MessageCircle className="h-4 w-4 text-[#0F0F12]" />
                {unread > 0 && (
                  <span data-testid="header-unread-badge"
                    className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-[#7C3AED] text-white text-[10px] font-medium grid place-items-center">
                    {unread > 99 ? "99+" : unread}
                  </span>
                )}
              </Link>
              <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button data-testid="header-user-menu"
                  className="h-9 w-9 rounded-full bg-[#0F0F12] text-white grid place-items-center font-medium">
                  {user.name?.[0]?.toUpperCase() || "U"}
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuLabel>
                  <div className="font-medium">{user.name}</div>
                  <div className="text-xs text-muted-foreground">{user.email}</div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => nav("/profile")} data-testid="menu-profile">
                  <UserIcon className="h-4 w-4 mr-2" /> My posts
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => nav("/settings")} data-testid="menu-settings">
                  <SettingsIcon className="h-4 w-4 mr-2" /> Settings
                </DropdownMenuItem>
                <DropdownMenuItem onClick={async () => { await logout(); nav("/"); }} data-testid="menu-logout">
                  <LogOut className="h-4 w-4 mr-2" /> Log out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            </>
          ) : (
            <Button asChild variant="ghost" size="sm" className="rounded-full" data-testid="header-login-btn">
              <Link to="/login">Log in</Link>
            </Button>
          )}
        </div>
      </div>
    </header>
  );
}
